/**
 * SAÚDE DO NÚMERO — o cálculo puro (spec 19).
 *
 * ## A pergunta
 *
 * *"Este número está indo bem, está sendo afunilado, ou está prestes a cair?"*
 *
 * ## Por que ela não tem resposta pronta
 *
 * **Nenhum dos dois canais diz quem denunciou.** No aparelho vinculado não existe nota nenhuma: o
 * único "report" da biblioteca é um HMAC que SAI com a nossa mensagem para o WhatsApp validar a
 * denúncia de quem a recebeu. No oficial existe nota — `quality_rating` — mas ela é **da Meta**, e
 * chega pronta, sem os números que a formaram.
 *
 * O que se pode fazer é o que a própria Meta declara fazer: ela calcula a nota com **bloqueios,
 * denúncias, taxa de leitura e o motivo do bloqueio**, em janela de 7 dias com peso por recência.
 * Nenhum desses quatro é observável de fora — mas três deles têm SOMBRA no que já gravamos:
 *
 * | O que a Meta olha | A sombra que vemos | Onde |
 * |---|---|---|
 * | bloqueio | mensagem enviada que nunca é entregue | `canal_envios` |
 * | taxa de leitura | a própria | `canal_envios` |
 * | denúncia | ninguém responde, e as saídas de grupo | `canal_mensagens`, `grupo_eventos` |
 *
 * ## As três regras que este arquivo não pode ceder
 *
 * 1. **N/A não é zero.** Indicador sem amostra **sai da conta** e aparece como não aplicável. Um
 *    número que não mandou nada esta semana não tem entrega ruim — não tem entrega. Tratá-lo como
 *    zero daria "risco" para o número mais saudável do sistema: o que ninguém usou.
 * 2. **Punição não desconta ponto — ela baixa o TETO.** Um ban ativo com 100% de entrega nos
 *    últimos dias ainda é um número banido. Descontar pontos deixaria a média boa esconder o fato;
 *    o teto o torna impossível de esconder.
 * 3. **Nota da Meta e índice nosso nunca se misturam.** GREEN é medida deles; o índice é inferência
 *    nossa. Somar os dois num número só venderia opinião como medida — e é o oposto do que a tela
 *    existe para fazer.
 *
 * ## Por que puro
 *
 * Sem SQL, sem relógio, sem `fetch`: entra o que foi medido, sai o veredito. É o que permite
 * exercitar a fronteira que interessa (sem amostra, um componente só, punição ativa, empate na
 * faixa) sem Postgres de pé — e é onde as mutações da prova batem.
 */

/** Cada indicador é uma fração 0..1 com o tamanho da amostra que a produziu. */
export interface Medida {
  /** `null` = NÃO MEDIDO. Nunca zero por ausência — ver a regra 1 do cabeçalho. */
  readonly valor: number | null;
  /** Quantas observações sustentam o valor. Zero e `valor: null` andam juntos. */
  readonly amostra: number;
}

/** O que a tela chama de "indicador", já com a régua de leitura embutida. */
export type NomeDeIndicador =
  | "entrega"
  | "leitura"
  | "resposta"
  | "reciprocidade"
  | "anomalias"
  | "saidasDeGrupo"
  | "rejeicaoEmGrupo"
  | "disponibilidade";

export interface IndicadorAvaliado {
  readonly nome: NomeDeIndicador;
  readonly valor: number | null;
  readonly amostra: number;
  /** O peso que ele teve NESTA conta. Zero quando saiu por falta de amostra. */
  readonly peso: number;
  /** 0..100, já orientado para "maior é melhor". `null` quando não entrou. */
  readonly nota: number | null;
  readonly aplicavel: boolean;
  /**
   * Por que ele NÃO entrou na conta. `null` quando entrou.
   *
   * As duas razões são diferentes na tela e na cabeça de quem lê: `sem_amostra` é *"não houve
   * envio"*; `amostra_pequena` é *"houve, e é pouco para concluir"* — e neste segundo caso o valor
   * medido continua VISÍVEL, porque ele é informação, só não é veredito.
   */
  readonly motivo: "sem_amostra" | "amostra_pequena" | null;
}

/**
 * Os pesos. **São nossos, e são revisáveis** — não há tabela da Meta para copiar.
 *
 * A ordem não é gosto: ela segue o que a Meta declara pesar, do mais duro para o mais macio.
 * Entrega é a sombra do bloqueio, e bloqueio é o sinal mais forte que existe do outro lado.
 * Disponibilidade fecha a lista porque ela mede a NOSSA infraestrutura, não a reputação do número —
 * mas um número que passa o dia fora do ar não está saudável, e omiti-la esconderia isso.
 *
 * ⚠️ Mexer aqui muda a série histórica sem mudar o mundo. Quem mexer, registra a data e o porquê:
 * uma nota que caiu porque o peso mudou é indistinguível de uma que caiu porque o número piorou.
 */
export const PESOS: Readonly<Record<NomeDeIndicador, number>> = {
  entrega: 30,
  resposta: 20,
  /**
   * **Saídas de grupo depois de uma publicação.** Peso alto para um indicador que quase nunca tem
   * amostra — e é de propósito.
   *
   * É o sinal mais DIRETO de "acharam isto spam" que este produto consegue observar: ninguém sai de
   * um grupo por acaso nos minutos seguintes a uma mensagem. Os outros indicadores são sombras
   * (bloqueio inferido pela não-entrega, denúncia inferida pelo silêncio); este é a própria pessoa
   * agindo, e o WhatsApp deixando ver.
   *
   * Ele só existe para quem publica em grupo. Para todos os outros é N/A e sai da conta — que é
   * exatamente o motivo de o peso poder ser alto sem penalizar ninguém: ele não entra onde não há
   * publicação.
   */
  saidasDeGrupo: 18,
  /**
   * **Rejeição em grupo** (2ª leva, 10/09): o nosso número tirado de um grupo por um admin, ou uma
   * mensagem nossa apagada por um admin — sobre as publicações em grupo do período.
   *
   * É o mesmo tipo de sinal que `saidasDeGrupo` — o outro lado AGINDO, não uma sombra —, e mais duro:
   * sair é o participante indo embora; isto é quem cuida do grupo dizendo que a nossa mensagem não
   * cabe ali. Peso um pouco abaixo porque a amostra é menor e o evento, mais raro; N/A para quem não
   * publica, pelo mesmo motivo do de cima.
   */
  rejeicaoEmGrupo: 15,
  leitura: 15,
  reciprocidade: 15,
  anomalias: 10,
  disponibilidade: 10,
};

/**
 * Os indicadores em que **menos é melhor**: os que contam coisa ruim acontecendo.
 *
 * Existe como conjunto, e não como `if`, porque o segundo indicador invertido é o que vai errar: o
 * `if` fica no lugar certo para um e some para o outro, e a nota sai ao contrário sem nada quebrar.
 */
const MENOS_E_MELHOR: ReadonlySet<NomeDeIndicador> = new Set<NomeDeIndicador>(["anomalias", "saidasDeGrupo", "rejeicaoEmGrupo"]);

/**
 * Os indicadores que falam da REPUTAÇÃO do número — e não da nossa infraestrutura.
 *
 * ## Por que esta lista existe (achado ao abrir a tela, 2026-09-10)
 *
 * `disponibilidade` **sempre** tem amostra: o tempo passou, e o número esteve de pé. Um número
 * recém-conectado, que nunca enviou nada, entrava na conta com esse único indicador em 100% — e a
 * tela dizia **"Bom, 100"** para o número sobre o qual não se sabe absolutamente nada.
 *
 * Era a regra 1 do cabeçalho vencida pelo outro lado: não por tratar ausência como zero, mas por
 * tratar ausência como CEM. E o estrago é maior, porque "risco" faz alguém olhar e "bom" faz não
 * olhar.
 *
 * Então o índice exige pelo menos um indicador de reputação com amostra. `disponibilidade` continua
 * pesando quando há reputação medida — um número que passa o dia fora do ar não está saudável —, mas
 * não sustenta o índice sozinha.
 */
const DE_REPUTACAO: ReadonlySet<NomeDeIndicador> = new Set<NomeDeIndicador>([
  "entrega",
  "resposta",
  "leitura",
  "reciprocidade",
  "anomalias",
  "saidasDeGrupo",
  "rejeicaoEmGrupo",
]);

/**
 * Quantas observações um indicador de TAXA precisa para virar veredito.
 *
 * ## Por que ele existe (achado EM PRODUÇÃO, 2026-09-10)
 *
 * No primeiro dia de dados reais, os dois números vivos apareceram em **Risco** — 57 e 59. Olhando
 * os componentes: entrega de 0% em **5 mensagens**, leitura de 0% em **5**, resposta de 25% em
 * **4**. Um número com 5 envios que teve azar em 5 não está em risco: não se sabe nada sobre ele.
 *
 * Pior, os números se contradiziam: o `5511910245347` tinha entrega 0% e **resposta 67%** — quem
 * respondeu, obviamente, recebeu. Com amostra assim, o índice não estava medindo o número; estava
 * medindo o acaso, e pintando de vermelho.
 *
 * ## Por que 20, e por que é régua nossa
 *
 * Abaixo de ~20 observações a margem de erro de uma proporção é maior que a distância entre as
 * faixas (80 e 60) — o indicador oscilaria de "bom" a "risco" com uma entrega a mais. **Não há
 * número publicado pela Meta para isto**, e inventar precisão estatística onde só há bom senso seria
 * o mesmo erro que a tela evita em todo o resto.
 *
 * ⚠️ `disponibilidade` NÃO passa por aqui: a amostra dela é TEMPO (segundos da janela), sempre
 * enorme, e o corte não teria sentido. É por isso que a lista abaixo é dos de TAXA, e não uma
 * constante solta que alguém aplicaria a tudo.
 */
export const AMOSTRA_MINIMA = 20;

/**
 * Os indicadores cuja amostra é contagem de mensagens ou de pessoas — e não tempo.
 *
 * ⚠️ **Exportado porque a TELA precisa da mesma resposta.** Enquanto ela não tinha, imprimia
 * `obs.` para todo mundo — e `disponibilidade`, cuja amostra são os segundos da janela, aparecia em
 * produção como **"604.800 obs."**: sete dias em segundos, apresentados como se fossem observações.
 * A unidade da amostra é parte da definição do indicador, e mora aqui com ela.
 */
export const AMOSTRA_EH_CONTAGEM: ReadonlySet<NomeDeIndicador> = new Set<NomeDeIndicador>([
  "entrega",
  "resposta",
  "leitura",
  "reciprocidade",
  "anomalias",
  "saidasDeGrupo",
  "rejeicaoEmGrupo",
]);

const DE_TAXA = AMOSTRA_EH_CONTAGEM;

/** O que puxa o TETO para baixo, e até quanto. Ver a regra 2 do cabeçalho. */
export type Punicao =
  | "banido_temporario"
  | "alcance_restrito"
  | "revogada"
  | "falha_de_conexao"
  | "sessao_substituida";

/**
 * O teto que cada punição ATIVA impõe. O menor de todos vence.
 *
 * `revogada` é 0 porque o número não está degradado: ele não existe mais para nós. `banido_temporario`
 * e `alcance_restrito` são 20 e 35 — os dois são o WhatsApp agindo sobre a conta, e a diferença é
 * que um proíbe e o outro afunila. `sessao_substituida` é 70 porque **não é punição do WhatsApp**:
 * são dois processos nossos disputando as mesmas chaves. Ela entra na lista porque o sintoma que
 * chega ao cliente é o mesmo — "o número cai" — e sair daqui a faria invisível justamente na tela
 * onde se procura por isso.
 */
export const TETO_POR_PUNICAO: Readonly<Record<Punicao, number>> = {
  revogada: 0,
  banido_temporario: 20,
  alcance_restrito: 35,
  falha_de_conexao: 40,
  sessao_substituida: 70,
};

export type Faixa = "bom" | "atencao" | "risco" | "sem_amostra";

/**
 * Quais sinais do contador diário são ANOMALIA — o numerador de `anomalias`.
 *
 * ⚠️ **Lista explícita, e não "todo sinal".** Até a 2ª leva a consulta somava tudo o que havia em
 * `numero_sinais_diarios`, e isso só estava certo porque os três sinais que existiam eram anomalia.
 * Com a 2ª leva o contador passou a guardar também conexão instável e rejeição em grupo — e somar
 * tudo faria uma queda de Wi-Fi pesar como mensagem que não se decifra, e contaria a mesma remoção
 * de grupo em dois indicadores. Sinal novo entra em indicador por DECISÃO, escrita aqui.
 */
export const SINAIS_DE_ANOMALIA: ReadonlySet<string> = new Set([
  "recibo_server_error",
  "recibo_retry",
  "mensagem_indecifravel",
  /** O servidor recusando o envio (2ª leva). O código vai cru no erro; aqui ele é só contagem. */
  "envio_recusado",
]);

/** Os sinais de rejeição em grupo — o numerador de `rejeicaoEmGrupo`. */
export const SINAIS_DE_REJEICAO_EM_GRUPO: ReadonlySet<string> = new Set(["removido_de_grupo", "apagada_por_admin"]);

export interface EntradaDeSaude {
  readonly entrega: Medida;
  readonly leitura: Medida;
  readonly resposta: Medida;
  readonly reciprocidade: Medida;
  readonly anomalias: Medida;
  /** Saídas em 24 h após uma publicação, sobre a audiência alcançada. N/A para quem não publica. */
  readonly saidasDeGrupo: Medida;
  /** Remoções do nosso número + mensagens nossas apagadas por admin, sobre as publicações em grupo. N/A sem publicação. */
  readonly rejeicaoEmGrupo: Medida;
  readonly disponibilidade: Medida;
  /** As punições ATIVAS agora — já filtradas por prazo por quem consultou. */
  readonly punicoesAtivas: readonly Punicao[];
}

export interface Saude {
  /** 0..100. **`null` = sem amostra nenhuma**, e a tela diz isso em vez de mostrar zero. */
  readonly indice: number | null;
  readonly faixa: Faixa;
  readonly indicadores: readonly IndicadorAvaliado[];
  /** Quantos indicadores entraram na conta, de quantos existem. É o que torna o índice auditável. */
  readonly componentes: { readonly usados: number; readonly total: number };
  /** O teto imposto por punição ativa, quando houve. `null` = nada limitou. */
  readonly teto: number | null;
  readonly punicoesAtivas: readonly Punicao[];
}

/**
 * Converte uma medida na nota 0..100 do indicador.
 *
 * `null` entra e `null` sai — a ausência atravessa o cálculo inteiro sem virar número em lugar
 * nenhum. É aqui que a regra 1 é imposta de fato; o resto do arquivo só a respeita.
 */
function notaDe(nome: NomeDeIndicador, m: Medida): number | null {
  if (m.valor === null || m.amostra <= 0) return null;
  const limitado = Math.max(0, Math.min(1, m.valor));
  const orientado = MENOS_E_MELHOR.has(nome) ? 1 - limitado : limitado;
  return Math.round(orientado * 100);
}

/**
 * A conta.
 *
 * Média ponderada **só do que tem amostra**, com o peso renormalizado pelo que sobrou — sem isso,
 * um número com um único indicador medido teria teto de 30 pontos e apareceria em risco por não ter
 * dado, que é a mesma armadilha da regra 1 disfarçada de aritmética.
 */
export function avaliarSaude(entrada: EntradaDeSaude): Saude {
  const nomes: readonly NomeDeIndicador[] = [
    "entrega",
    "resposta",
    "saidasDeGrupo",
    "rejeicaoEmGrupo",
    "leitura",
    "reciprocidade",
    "anomalias",
    "disponibilidade",
  ];

  const indicadores: IndicadorAvaliado[] = nomes.map((nome) => {
    const m = entrada[nome];
    const nota = notaDe(nome, m);
    // Amostra pequena não apaga o VALOR — ela tira o direito de virar veredito. O valor continua
    // na tela, com o motivo ao lado; o que ele não faz é puxar a faixa do número inteiro.
    const pequena = nota !== null && DE_TAXA.has(nome) && m.amostra < AMOSTRA_MINIMA;
    const aplicavel = nota !== null && !pequena;
    return {
      nome,
      valor: m.valor,
      amostra: m.amostra,
      /*
       * O peso é o do INDICADOR, e não o que ele conseguiu exercer.
       *
       * Antes ia `aplicavel ? PESOS[nome] : 0`, e era a mesma armadilha que esta spec inteira
       * combate: **ausência virando zero**. O peso de `leitura` é 15 tenha ela contado ou não; quem
       * responde se contou é `aplicavel`, que já vai ao lado. Zerando aqui, a tela recebia `0` e não
       * tinha como dizer *"este valia 15 e ficou de fora"* — que é exatamente o que ela precisa
       * dizer para a coluna "Peso" significar alguma coisa.
       *
       * Não muda conta nenhuma: a soma abaixo corre só sobre `usados`, que são os aplicáveis — e
       * para esses os dois valores sempre foram o mesmo.
       */
      peso: PESOS[nome],
      nota,
      aplicavel,
      motivo: aplicavel ? null : pequena ? "amostra_pequena" : "sem_amostra",
    };
  });

  const usados = indicadores.filter((i) => i.aplicavel);
  const pesoTotal = usados.reduce((s, i) => s + i.peso, 0);
  const temReputacao = usados.some((i) => DE_REPUTACAO.has(i.nome));

  const teto = tetoDe(entrada.punicoesAtivas);

  if (usados.length === 0 || pesoTotal === 0 || !temReputacao) {
    /*
     * Sem amostra de REPUTAÇÃO não existe índice — e é diferente de índice baixo.
     *
     * `componentes.usados` continua contando o que foi medido de verdade (a disponibilidade, quando
     * ela sozinha caiu aqui): a tela mostra "0 de 6" quando não há nada, e o valor da
     * disponibilidade continua visível na lista. O que ela NÃO faz é virar nota.
     *
     * ⚠️ A punição continua atravessando mesmo aqui. Um número revogado ontem não mandou nada hoje,
     * então ele cairia exatamente neste ramo: dizer "sem amostra" e esconder a revogação seria
     * perder o único fato que a tela tinha para contar.
     */
    return {
      indice: null,
      faixa: "sem_amostra",
      indicadores,
      componentes: { usados: usados.filter((i) => DE_REPUTACAO.has(i.nome)).length, total: nomes.length },
      teto,
      punicoesAtivas: entrada.punicoesAtivas,
    };
  }

  const bruto = usados.reduce((s, i) => s + (i.nota ?? 0) * i.peso, 0) / pesoTotal;
  const indice = teto === null ? Math.round(bruto) : Math.min(Math.round(bruto), teto);

  return {
    indice,
    faixa: faixaDe(indice),
    indicadores,
    componentes: { usados: usados.length, total: nomes.length },
    teto,
    punicoesAtivas: entrada.punicoesAtivas,
  };
}

/** O menor teto entre as punições ativas. `null` quando não há punição. */
export function tetoDe(punicoes: readonly Punicao[]): number | null {
  let teto: number | null = null;
  for (const p of punicoes) {
    const t = TETO_POR_PUNICAO[p];
    // Punição que a versão de hoje não conhece não pode virar teto zero por `undefined`: ela é
    // ignorada no cálculo e continua VISÍVEL na lista, que é onde ela informa.
    if (typeof t !== "number") continue;
    teto = teto === null ? t : Math.min(teto, t);
  }
  return teto;
}

/**
 * As faixas.
 *
 * 80 e 60 são corte nosso, e são o que a tela pinta. Eles existem porque "72" não diz a ninguém se
 * é para agir hoje; a faixa diz. O valor continua visível ao lado, para quem acompanha a curva.
 */
export function faixaDe(indice: number): Faixa {
  if (indice >= 80) return "bom";
  if (indice >= 60) return "atencao";
  return "risco";
}

/**
 * A frase que a tela mostra. Uma por faixa, e a punição vence a faixa quando existe.
 *
 * Ela mora aqui, e não no componente, porque é a mesma frase que o relatório e a API precisam dizer
 * — e porque frase duplicada em dois lugares só é igual até alguém editar um deles.
 */
export function frasePara(s: Saude): string {
  if (s.punicoesAtivas.includes("revogada")) {
    return "Este número foi desvinculado: só um pareamento novo o traz de volta.";
  }
  if (s.punicoesAtivas.includes("banido_temporario")) {
    return "O WhatsApp bloqueou este número temporariamente. Não envie nada até o prazo terminar.";
  }
  if (s.punicoesAtivas.includes("alcance_restrito")) {
    return "O WhatsApp restringiu quantas pessoas novas este número pode abordar.";
  }
  if (s.indice === null) {
    // A frase distingue as duas ausências: quem não enviou nada, e quem enviou pouco. A segunda é a
    // mais comum no começo, e dizer "não houve envio" para quem enviou 5 faria a tela parecer quebrada.
    const pouco = s.indicadores.some((i) => i.motivo === "amostra_pequena");
    return pouco
      ? "Houve envio, mas ainda é pouco para concluir alguma coisa sobre este número."
      : "Ainda não há envio suficiente nesta janela para medir a saúde deste número.";
  }
  if (s.faixa === "bom") return "Os indicadores desta janela não apontam problema.";
  if (s.faixa === "atencao") {
    return "Alguns indicadores caíram. Vale olhar antes de aumentar o volume.";
  }
  return "Os indicadores estão ruins. Aumentar o volume agora aumenta o risco de bloqueio.";
}
